/**
 * Outcome and progress panels for the three wired flows: certification exams,
 * NPC levels, and training assignments.
 *
 * Presentational only (no hooks), so they render to static markup in tests.
 * The rule they share: an unknown is never drawn as a fail or a 0. A level
 * whose overall could not be measured is "not evaluable"; an assignment whose
 * required score was not measured "stays in progress"; an exam with no graded
 * session counted yet has no average.
 */

import { Link } from 'react-router-dom';
import type { LevelOutcome } from '../../store/api/coachingApi';
import type { TrainingOutcome } from '../../store/api/practiceApi';
import type { DomainExamDefinition, ExamProgressResponse } from '../../store/api/credentialsApi';
import { examConversationUrl, formatSeconds } from '../../utils/flowLinks';

// ─── NPC level ──────────────────────────────────────────────────────────────

export function LevelOutcomeCard({ outcome, levelName }: { outcome: LevelOutcome; levelName?: string }) {
  const name = levelName ?? 'this level';
  if ('notEvaluable' in outcome) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4" data-testid="level-outcome" data-outcome="not_evaluable">
        <p className="font-semibold text-gray-900">Not evaluable</p>
        <p className="mt-1 text-sm text-gray-700">
          The overall score for this conversation could not be measured, so {name} was not judged.
          This attempt does not count as a fail.
        </p>
      </div>
    );
  }
  if (outcome.passed) {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-4" data-testid="level-outcome" data-outcome="passed">
        <p className="font-semibold text-green-900">
          {outcome.newlyPassed ? `Level passed: ${name}` : `Passed: ${name}`}
        </p>
        <p className="mt-1 text-sm text-green-800">
          Best score {Math.round(outcome.currentBestScore)} (needed {outcome.requiredScore})
          {outcome.xpAwarded > 0 ? ` · +${outcome.xpAwarded} XP` : ''}
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="level-outcome" data-outcome="not_yet">
      <p className="font-semibold text-amber-900">Not yet: {name}</p>
      <p className="mt-1 text-sm text-amber-800">
        You need {outcome.requiredScore}. Your best so far is {Math.round(outcome.currentBestScore)}.
      </p>
    </div>
  );
}

// ─── Training assignment ────────────────────────────────────────────────────

export function AssignmentContextBanner({
  moduleName,
  minOverallScore,
}: {
  moduleName: string | null;
  minOverallScore?: number | null;
}) {
  return (
    <div className="rounded-lg border border-primary-200 bg-primary-50 p-4" data-testid="assignment-context">
      <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">Training assignment</p>
      <p className="mt-1 font-medium text-gray-900">
        Practising: {moduleName ?? 'your assigned training module'}
      </p>
      <p className="mt-1 text-sm text-gray-600">
        This Record &amp; Review session counts toward the assignment
        {typeof minOverallScore === 'number' ? ` (pass at ${minOverallScore}%)` : ''}.
      </p>
    </div>
  );
}

const ASSIGNMENT_COPY: Record<TrainingOutcome['outcome'], { title: string; body: string; tone: string }> = {
  completed: {
    title: 'Assignment completed',
    body: 'This session met every requirement of the assignment.',
    tone: 'border-green-200 bg-green-50 text-green-900',
  },
  failed: {
    title: 'Assignment not passed',
    body: 'A measured score was below the assignment threshold. You can retry it from My Training.',
    tone: 'border-red-200 bg-red-50 text-red-900',
  },
  unmeasured: {
    title: 'Assignment still in progress',
    body: 'A required score could not be measured in this session, so it was not judged. It is not a fail — practise again to record a result.',
    tone: 'border-gray-200 bg-gray-50 text-gray-900',
  },
  already_completed: {
    title: 'Assignment already completed',
    body: 'This assignment was completed before this session; its result is unchanged.',
    tone: 'border-green-200 bg-green-50 text-green-900',
  },
};

export function AssignmentOutcomeCard({ outcome }: { outcome: TrainingOutcome | null }) {
  if (!outcome) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="assignment-outcome" data-outcome="unknown">
        <p className="font-semibold text-amber-900">Assignment result unavailable</p>
        <p className="mt-1 text-sm text-amber-800">
          Your session was saved, but its effect on the assignment could not be confirmed. Check My Training.
        </p>
        <Link to="/my-training" className="mt-2 inline-block text-sm font-medium text-amber-900 underline">Go to My Training</Link>
      </div>
    );
  }
  const copy = ASSIGNMENT_COPY[outcome.outcome];
  return (
    <div className={`rounded-lg border p-4 ${copy.tone}`} data-testid="assignment-outcome" data-outcome={outcome.outcome}>
      <p className="font-semibold">{copy.title}</p>
      <p className="mt-1 text-sm">{copy.body}</p>
      <Link to="/my-training" className="mt-2 inline-block text-sm font-medium underline">Back to My Training</Link>
    </div>
  );
}

// ─── Certification exam ─────────────────────────────────────────────────────

export const NO_EXAM_SCENARIO_MESSAGE =
  'No graded exam scenario has been authored for this certification yet.';

export function ExamProgressPanel({
  exam,
  progress,
}: {
  exam: Pick<DomainExamDefinition, 'label' | 'minSessions' | 'minConversationLengthSeconds' | 'passingScoreOverall'>;
  progress: ExamProgressResponse | null;
}) {
  const sessions = progress?.sessionsCompleted ?? 0;
  const seconds = progress?.totalConversationSeconds ?? 0;
  const minSessions = progress?.minSessionsRequired ?? exam.minSessions;
  const minSeconds = progress?.minConversationSecondsRequired ?? exam.minConversationLengthSeconds;
  const passing = progress?.passingScore ?? exam.passingScoreOverall;
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4" data-testid="exam-progress">
      <p className="font-semibold text-gray-900">Progress toward {exam.label}</p>
      <dl className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
        <div>
          <dt className="text-gray-500">Graded sessions</dt>
          <dd className="font-medium text-gray-900" data-testid="exam-sessions">{sessions} / {minSessions}</dd>
        </div>
        <div>
          <dt className="text-gray-500">Conversation time</dt>
          <dd className="font-medium text-gray-900" data-testid="exam-duration">
            {formatSeconds(seconds)} / {formatSeconds(minSeconds)}
          </dd>
        </div>
        <div>
          <dt className="text-gray-500">Average score</dt>
          <dd className="font-medium text-gray-900" data-testid="exam-score">
            {sessions > 0 && progress ? `${progress.currentAvgScore} (pass at ${passing})` : `No graded session yet (pass at ${passing})`}
          </dd>
        </div>
      </dl>
      {progress?.isReadyToComplete && (
        <p className="mt-3 text-sm text-green-700">Session and time requirements met — you can complete the exam.</p>
      )}
    </div>
  );
}

export function ExamScenarioList({
  domainKey,
  scenarios,
  isLoading,
}: {
  domainKey: string;
  scenarios: ReadonlyArray<{ id: string; title: string; description?: string; estimatedMinutes?: number }>;
  isLoading: boolean;
}) {
  if (isLoading) return <p className="text-sm text-gray-500">Loading exam scenarios…</p>;
  if (scenarios.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4" data-testid="exam-no-scenario">
        <p className="text-sm text-gray-700">{NO_EXAM_SCENARIO_MESSAGE}</p>
      </div>
    );
  }
  return (
    <ul className="space-y-2" data-testid="exam-scenarios">
      {scenarios.map((s) => (
        <li key={s.id} className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-3">
          <div className="min-w-0">
            <p className="font-medium text-gray-900">{s.title}</p>
            {s.description && <p className="text-sm text-gray-500 line-clamp-1">{s.description}</p>}
          </div>
          <Link to={examConversationUrl(s.id, domainKey)} className="btn-primary px-3 py-1.5 text-sm whitespace-nowrap">
            Take graded attempt
          </Link>
        </li>
      ))}
    </ul>
  );
}
